import { keyframes } from '@mui/material';

/**
 * Animation utilities for the Activation Checklist
 *
 * Design Philosophy:
 * - Restrained elegance (Stripe/Linear/Notion quality)
 * - Purposeful motion that communicates state
 * - Professional delight without being playful
 * - Fast and fluid (under 300ms)
 */

// =============================================================================
// KEYFRAME ANIMATIONS
// =============================================================================

/** Subtle entrance fade */
export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/** Smooth slide + fade for expanding content */
export const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** SVG checkmark stroke draw animation */
export const checkDraw = keyframes`
  from {
    stroke-dashoffset: 24;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

/** Scale bounce for completion celebrations */
export const popIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/** Moving highlight shine on progress bar */
export const progressShine = keyframes`
  from {
    background-position: -200% 0;
  }
  to {
    background-position: 200% 0;
  }
`;

/** Gentle attention pulse for current step (not aggressive) */
export const gentlePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(99, 91, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(99, 91, 255, 0);
  }
`;

/** Success celebration burst behind checkmark */
export const celebrateBurst = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

/** Icon rotation for expand/collapse */
export const rotateDown = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
`;

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

export const TIMING = {
  /** 100ms - Micro-interactions */
  instant: '100ms',
  /** 150ms - Quick feedback */
  fast: '150ms',
  /** 200ms - Standard transitions */
  normal: '200ms',
  /** 300ms - Smooth animations */
  smooth: '300ms',
  /** 400ms - Slower, deliberate animations */
  slow: '400ms',
} as const;

// =============================================================================
// EASING CURVES
// =============================================================================

export const EASING = {
  /** Standard smooth easing */
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Bouncy overshoot for celebrations */
  bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** Quick start, slow end */
  decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  /** Slow start, quick end */
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

// =============================================================================
// COLORS
// =============================================================================

/** BLURPLE - Premium accent color matching theme VIP patterns */
export const BLURPLE = '#635bff';

// =============================================================================
// REDUCED MOTION HELPER
// =============================================================================

/**
 * Returns sx props that respect prefers-reduced-motion
 * Usage: sx={{ ...getReducedMotionProps(animation, duration) }}
 */
export const getReducedMotionProps = (
  animation: ReturnType<typeof keyframes>,
  duration: string,
  easing: string = EASING.smooth
) => ({
  animation: `${animation} ${duration} ${easing}`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transition: 'opacity 0.1s',
  },
});
